import { DatePipe, NgClass, NgForOf, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatInput } from '@angular/material/input';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef,
  MatTable,
  MatTableDataSource
} from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { BehaviorSubject, timer } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { AdminService } from '../../admin/admin.service';
import { DataToolsApps, DataToolsAppsTitles } from '../../shared/DataToolsApps';
import { IDedupeTask } from '../../shared/interfaces/duplikit-interfaces';
import { KyloeConfirmDialogComponent } from '../../shared/kyloe-confirm-dialog/kyloe-confirm-dialog.component';
import { DuplikitMergeService } from '../../shared/services/duplikit-merge.service';
import { DuplikitUndedupeService } from '../../shared/services/duplikit-undedupe.service';
import { MessageNotificationService } from '../../shared/services/message-notification.service';
import { DuplikitHistoryAllByMasterIdComponent } from './duplikit-history-all-by-master-id/duplikit-history-all-by-master-id.component';
import { DuplikitMergeLogService } from './duplikit-merge-log.service';

@Component({
  selector: 'app-duplikit-history',
  templateUrl: './duplikit-history.component.html',
  imports: [
    MatButtonToggleGroup,
    MatButtonToggle,
    NgForOf,
    NgClass,
    NgIf,
    NgSwitch,
    NgSwitchCase,
    NgSwitchDefault,
    MatTable,
    MatSort,
    MatHeaderCell,
    MatColumnDef,
    MatHeaderCellDef,
    MatCellDef,
    MatCell,
    DatePipe,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatPaginator,
    MatTooltip,
    MatInput,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSortModule
  ],
  styleUrls: ['./duplikit-history.component.scss']
})
export class DuplikitHistoryComponent implements OnInit, OnDestroy {
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild('dateSortValue') dateSortValue: ElementRef; // TODO: can be done with data binding instead?
  @ViewChild('masterIdValue') masterIdValue: ElementRef;
  @ViewChild('duplicateIdValue') duplicateIdValue: ElementRef;
  @ViewChild('userIdValue') userIdValue: ElementRef;

  public displayedColumns: string[] = [
    'masterId',
    'duplicates',
    'userId',
    'date',
    'state'
  ];
  public filterColumns: string[] = [
    'masterId_filter',
    'duplicates_filter',
    'userId_filter',
    'date_filter',
    'state_filter'
  ];
  public dataSource: MatTableDataSource<any>;
  public entityDedupeTasks: IDedupeTask[] = [];
  public mergeHistoryLoading = true;
  public inputFilterData = {
    masterId: null,
    duplicateId: null,
    userId: null,
    date: null as Date,
    mergerUserId: null
  };
  public selectedEntity = 'Candidate';
  public pageSizeOptions = [10, 20, 50];
  public entities = [
    { name: 'Candidate', total: null, queued : null },
    { name: 'Contact', total: null, queued : null },
    { name: 'Company', total: null, queued : null }
  ];
  DataToolsApps = DataToolsApps;
  DataToolsAppsTitles = DataToolsAppsTitles;
  private filterDataSubject = new BehaviorSubject<{}>(this.inputFilterData);

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private mergeLogService: DuplikitMergeLogService,
    private duplikitUndedupeService: DuplikitUndedupeService,
    private messageNotificationService: MessageNotificationService,
    public duplikitMergeService: DuplikitMergeService,
    private adminService: AdminService
  ) {
    this.dataSource = new MatTableDataSource();
  }

  ngOnInit() {
    if (this.adminService.isInLiteMode()) {
      this.setupForLitePreview();
      return;
    }
    this.getMergeLogCounts();
    this.filterDataSubject.pipe(
      debounceTime(1000), // TODO: should only debounce on key input and not clicks? i.e. changing date.
      map(() => this.mergeHistoryLoading = true),
      debounceTime(150) // to make it clear a refresh has happened.
    ).subscribe((_) => {
      this.fetchData();
    });
    timer(0, 10000).subscribe(() => {
      this.checkForCompleteUnDedupeTasks();
    }, (error) => {
      console.error(error);
    });
    if (!this.adminService.isInLiteMode()) {
      this.duplikitMergeService.runMergeCheck();
    }
  }

  goToApp(pca: DataToolsApps): void {
    this.router.navigate([`/${pca.toString()}`]);
  }

  getMergeLogCounts(): void {
    this.mergeLogService.getMergeCountsForEachEntity().subscribe((dataReceived) => {
      this.entities[0].total = dataReceived.Candidate.total;
      this.entities[0].queued = dataReceived.Candidate.queued;
      this.entities[1].total = dataReceived.ClientContact.total;
      this.entities[1].queued = dataReceived.ClientContact.queued;
      this.entities[2].total = dataReceived.ClientCorporation.total;
      this.entities[2].queued = dataReceived.ClientCorporation.queued;
    });
  }

  showMergeLogs(dedupeTaskId): void {
    this.dialog.open(DuplikitHistoryAllByMasterIdComponent, {
      width: '45vw',
      height: 'auto',
      autoFocus: true,
      data: dedupeTaskId
    });
  }

  onEntitySelected(entity: string): void {
    this.selectedEntity = entity;
    this.resetFilters();
    this.fetchData();
  }

  fetchData(): void {
    if (this.adminService.isInLiteMode()) {
      this.dataSource = new MatTableDataSource([]);
      return;
    }
    switch (this.selectedEntity) {
      case 'Contact':
        this.getEntityMergeLog('ClientContact');
        break;
      case 'Company':
        this.getEntityMergeLog('ClientCorporation');
        break;
      default:
        this.getEntityMergeLog('Candidate');
        break;
    }
  }

  getClassNameForEntity(entity) {
    entity = entity.toLowerCase();
    if (entity === 'company') {
      return 'clientcorporation-entity';
    } else {
      return `${entity}-entity`;
    }
  }

  private renderIcon(entity): string { // TODO: private and called from the template and why is this not a switch?
    entity = entity.toLowerCase();
    if (entity.includes('candidate'))
      return 'bhi-candidate';
    if (entity.includes('contact'))
      return 'bhi-person';
    if (entity.includes('company'))
      return 'bhi-company';
    if (entity.includes('job'))
      return 'bhi-job';
    if (entity.includes('lead'))
      return 'bhi-lead';
    if (entity.includes('opportunity'))
      return 'bhi-opportunity';
    if (entity.includes('placement'))
      return 'bhi-star';
    if (entity.includes('appointment'))
      return 'bhi-appointment';
    if (entity.includes('task'))
      return 'bhi-check-o';
    return 'bhi-puzzle';
  }

  private populateDedupeTasks(): void {
    this.mergeHistoryLoading = true;
    this.setupTable(this.entityDedupeTasks);
  }

  private getEntityMergeLog(entity: string): void {
    this.mergeHistoryLoading = true;
    this.mergeLogService.getLogForEntity(entity, this.inputFilterData.date, this.inputFilterData.masterId, this.inputFilterData.duplicateId, this.inputFilterData.userId)
      .subscribe((dataReceived) => {
        console.log('Received log data for ' + entity, dataReceived);
        this.entityDedupeTasks = dataReceived;
        this.populateUnDedupingTasks();
        this.populateDedupeTasks();
      });
  }

  private populateUnDedupingTasks() {
    const revertingTasks: string[] = this.entityDedupeTasks
      .filter((mergeLog: IDedupeTask) => mergeLog.state === 'REVERTING')
      .map((mergeLog: IDedupeTask) => mergeLog.id);
    this.duplikitUndedupeService.unDedupingTasks.push(...revertingTasks);
  }

  private setupTable(entityDedupeTasks: IDedupeTask[]): void {
    entityDedupeTasks.sort((a, b) => {
      if (a.createdDate < b.createdDate) return 1;
      if (a.createdDate > b.createdDate) return -1;
      return 0;
    });
    this.dataSource = new MatTableDataSource(entityDedupeTasks);
    this.dataSource.sortingDataAccessor = (data: any, sortHeaderId: string) => {
      return sortHeaderId === 'date' ? data.endDate : data[sortHeaderId];
    };
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    this.mergeHistoryLoading = false;
  }

  resetDateFilter(): void {
    this.inputFilterData.date = null;
    this.dateSortValue.nativeElement.value = null;
    this.filterDataSubject.next(this.inputFilterData);
  }

  resetFilters(): void {
    this.inputFilterData = {
      masterId: null,
      duplicateId: null,
      userId: null,
      date: null,
      mergerUserId: null
    };
    this.dateSortValue.nativeElement.value = null;
    this.masterIdValue.nativeElement.value = null;
    this.duplicateIdValue.nativeElement.value = null;
    this.userIdValue.nativeElement.value = null;
  }

  updateFilter(column: string, value: string): void {
    if (column === 'date') {
      if (value !== '') {
        this.inputFilterData.date = new Date(value);
      } else {
        this.inputFilterData.date = new Date('1970-01-01 00:00:00');
      }
    } else {
      this.inputFilterData[column] = value;
    }
    this.filterDataSubject.next(this.inputFilterData);
  }

  anyActiveFilter(): boolean {
    return (this.inputFilterData.masterId
      || this.inputFilterData.duplicateId
      || this.inputFilterData.userId
      || this.inputFilterData.date
      || this.inputFilterData.mergerUserId
    );
  }

  clearFiltersAndReload(): void {
    this.resetFilters();
    this.fetchData();
  }

  unDedupeByDedupeTaskId(log): void {

    const dialogRef = this.dialog.open(KyloeConfirmDialogComponent, {
      width: '55vw',
      height: 'auto',
      autoFocus: true,
      data: {
        titleIcon: 'History-KyloeIcon',
        title: 'Revert?',
        message: 'Are you sure you want to revert this merge?'
      }
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (!data) {
        return;
      } else {
        this.duplikitUndedupeService.unDedupingTasks.push(log.id);
        this.setDedupeTaskState(log.id, 'REVERTING');
        this.duplikitUndedupeService.unDedupeByDedupeTaskId(log.id).subscribe((res) => {
          this.messageNotificationService.info('Revert Merge', `Now reverting merge for ${log.entity} #${log.masterId}`);
        }, (error) => {
          let errorMessage = `There was an error reverting merge for ${log.entity} #${log.masterId}, please try again.`;
          if (error.error.message) {
            errorMessage = error.error.message;
          }
          this.messageNotificationService.error('Revert Error', errorMessage);
          this.removeFromUnDedupingList(log.id);
          this.fetchData();
          console.error(error);
        });
      }
    });
  }

  isUnDeduping(dedupeTask: IDedupeTask): boolean {
    return dedupeTask.state === 'REVERTING' && this.duplikitUndedupeService.unDedupingTasks.includes(dedupeTask.id);
  }

  checkForCompleteUnDedupeTasks(): void {
    this.duplikitUndedupeService.unDedupingTasks.forEach((dedupeTaskId) => {
      this.duplikitUndedupeService.unDedupeTaskIsComplete(dedupeTaskId).subscribe((res) => {
        if (res['isComplete']) {
          const log = this.entityDedupeTasks.find((dedupeTask: IDedupeTask) => dedupeTask.id === dedupeTaskId);
          if (log) {
            this.messageNotificationService.success('Revert Complete', `Completed Revert for ${ log.entity } #${ log.masterId }.`);
            this.removeFromUnDedupingList(dedupeTaskId);
            this.setDedupeTaskState(dedupeTaskId, 'REVERTED');
          }
        }
      });
    });
  }

  canRevert(dedupeTask: IDedupeTask): boolean {
    return dedupeTask.state === 'COMPLETE' && !this.isUnDeduping(dedupeTask);
  }

  aRevertIsInProgress(): boolean {
    return this.duplikitUndedupeService.unDedupingTasks.length > 0;
  }

  private removeFromUnDedupingList(dedupeTaskId: string): void {
    const index = this.duplikitUndedupeService.unDedupingTasks.indexOf(dedupeTaskId);
    console.log(this.duplikitUndedupeService.unDedupingTasks);
    this.duplikitUndedupeService.unDedupingTasks.splice(index, 1);
    console.log(this.duplikitUndedupeService.unDedupingTasks);
  }

  private setDedupeTaskState(dedupeTaskId: string, state: string) {
    this.entityDedupeTasks.find((mergeLog) => mergeLog.id === dedupeTaskId).state = state;
  }

  ngOnDestroy() {
    this.duplikitMergeService.stopMergeCheck();
  }

  private setupForLitePreview() {
    this.entities.forEach((entity) => {
      entity.total = 0;
      entity.queued = 0;
    });
    this.mergeHistoryLoading = false;
  }
}
